import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Badge } from '@vaiu/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@vaiu/ui/select';
import { Textarea } from '@vaiu/ui/textarea';
import { Switch } from '@vaiu/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@vaiu/ui/tabs';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { i18n } from '@/utils/i18n';
import ExchangeMatch from '@/components/ExchangeMatch';
import {
  ArrowLeft,
  Plus,
  ArrowRightLeft,
  Clock,
  Users,
  TrendingUp,
  Filter,
  Search,
  AlertCircle,
  CheckCircle,
  Gift,
  DollarSign,
  Euro,
  Globe,
  LineChart
} from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@vaiu/ui/radio-group';

interface ExchangeListing {
  id: string;
  type: 'offer' | 'request';
  userId: string;
  username: string;
  amount: number;
  currency: string;
  wantedAmount?: number;
  wantedCurrency?: string;
  description: string;
  timeLimit: string; // '1h', '6h', '1d', '3d', '1w'
  acceptPartial: boolean;
  onlyFullAmount: boolean;
  createdAt: string;
  expiresAt: string;
  status: 'active' | 'matched' | 'expired' | 'cancelled';
  matches?: number;
}

const ExchangePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [listings, setListings] = useState<ExchangeListing[]>([]);
  const [matches, setMatches] = useState<any[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCurrency, setFilterCurrency] = useState('all');
  const createFormRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const [filterType, setFilterType] = useState('all');
  
  // Form state
  const [formData, setFormData] = useState({
    type: 'offer' as 'offer' | 'request',
    amount: '',
    currency: 'USD',
    wantedAmount: '',
    wantedCurrency: 'EUR',
    description: '',
    timeLimit: '1d',
    acceptPartial: true,
    onlyFullAmount: false
  });

  useEffect(() => {
    loadExchangeListings();
  }, []);

  const loadExchangeListings = async () => {
    setLoading(true);
    try {
      // Mock data for now - in real app, this would fetch from API
      const mockListings: ExchangeListing[] = [
        {
          id: '1',
          type: 'offer',
          userId: 'user1',
          username: 'alice_trader',
          amount: 100,
          currency: 'USD',
          wantedAmount: 85,
          wantedCurrency: 'EUR',
          description: 'Quick USD to EUR exchange, good rates',
          timeLimit: '1d',
          acceptPartial: true,
          onlyFullAmount: false,
          createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          expiresAt: new Date(Date.now() + 22 * 60 * 60 * 1000).toISOString(),
          status: 'active',
          matches: 3
        },
        {
          id: '2',
          type: 'request',
          userId: 'user2',
          username: 'bob_merchant',
          amount: 50,
          currency: 'EUR',
          wantedAmount: 55,
          wantedCurrency: 'USD',
          description: 'Need USD for business payments',
          timeLimit: '6h',
          acceptPartial: false,
          onlyFullAmount: true,
          createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
          expiresAt: new Date(Date.now() + 5 * 60 * 60 * 1000).toISOString(),
          status: 'active',
          matches: 1
        },
        {
          id: '3',
          type: 'offer',
          userId: 'user3',
          username: 'crypto_enthusiast',
          amount: 200,
          currency: 'EUR',
          wantedAmount: 230,
          wantedCurrency: 'USD',
          description: 'Large EUR to USD exchange, flexible on amounts',
          timeLimit: '3d',
          acceptPartial: true,
          onlyFullAmount: false,
          createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
          expiresAt: new Date(Date.now() + 71 * 60 * 60 * 1000).toISOString(),
          status: 'active',
          matches: 7
        }
      ];
      setListings(mockListings);
    } catch (error) {
      console.error('Failed to load exchange listings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load exchange listings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateListing = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const timeMultiplier = {
        '1h': 1,
        '6h': 6,
        '1d': 24,
        '3d': 72,
        '1w': 168
      };
      
      const newListing: ExchangeListing = {
        id: Date.now().toString(),
        type: formData.type,
        userId: user?._id || 'current_user',
        username: user?.username || 'current_user',
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        wantedAmount: formData.wantedAmount ? parseFloat(formData.wantedAmount) : undefined,
        wantedCurrency: formData.wantedCurrency,
        description: formData.description,
        timeLimit: formData.timeLimit,
        acceptPartial: formData.acceptPartial,
        onlyFullAmount: formData.onlyFullAmount,
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + timeMultiplier[formData.timeLimit] * 60 * 60 * 1000).toISOString(),
        status: 'active',
        matches: 0
      };
      
      // In real app, this would POST to API
      setListings(prev => [newListing, ...prev]);
      
      // Reset form
      setFormData({
        type: 'offer',
        amount: '',
        currency: 'USD',
        wantedAmount: '',
        wantedCurrency: 'EUR',
        description: '',
        timeLimit: '1d',
        acceptPartial: true,
        onlyFullAmount: false
      });
      
      setShowCreateForm(false);
      
      toast({
        title: 'Listing Created',
        description: `Your ${formData.type} has been posted to the exchange`,
      });
    } catch (error) {
      console.error('Failed to create listing:', error);
      toast({
        title: 'Error',
        description: 'Failed to create listing',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMatchCreated = (match: any) => {
    setMatches(prev => [...prev, match]);
    
    // Update the listing's match count
    setListings(prev => prev.map(listing => 
      listing.id === match.listingId 
        ? { ...listing, matches: (listing.matches || 0) + 1 }
        : listing
    ));
  };

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      default: return currency + ' ';
    }
  };

  const getTimeRemaining = (expiresAt: string) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();
    
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'matched': return 'bg-blue-100 text-blue-800';
      case 'expired': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredListings = listings.filter(listing => {
    const matchesSearch = listing.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         listing.username.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCurrency = filterCurrency === 'all' || 
                           listing.currency === filterCurrency || 
                           listing.wantedCurrency === filterCurrency;
    const matchesType = filterType === 'all' || listing.type === filterType;
    
    return matchesSearch && matchesCurrency && matchesType;
  });

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 flex items-center justify-center">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please log in to access the token exchange.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back Home</span>
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Currency Exchange</h1>
              <p className="text-gray-600">Trade tokens with other users</p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/currency-market')}
              className="flex items-center space-x-2"
            >
              <LineChart className="w-4 h-4" />
              <span>Currency Market</span>
            </Button>
          </div>
          <Button
            onClick={() => {
              if (showCreateForm) {
                setShowCreateForm(false);
              } else {
                setShowCreateForm(true);
                // Smooth scroll to form after a brief delay for state update
                setTimeout(() => {
                  createFormRef.current?.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                  });
                  // Focus on first input after scroll
                  setTimeout(() => {
                    firstInputRef.current?.focus();
                  }, 500);
                }, 100);
              }
            }}
            className={`flex items-center space-x-2 ${showCreateForm ? 'bg-red-500 hover:bg-red-600' : ''}`}
            variant={showCreateForm ? 'destructive' : 'default'}
          >
            <Plus className={`w-4 h-4 transition-transform duration-200 ${showCreateForm ? 'rotate-45' : ''}`} />
            <span>{showCreateForm ? 'Cancel' : 'Create Listing'}</span>
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <ArrowRightLeft className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{listings.length}</p>
                  <p className="text-gray-600">Active Listings</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{listings.reduce((acc, l) => acc + (l.matches || 0), 0)}</p>
                  <p className="text-gray-600">Total Matches</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-purple-100 rounded-full">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{new Set(listings.map(l => l.userId)).size}</p>
                  <p className="text-gray-600">Active Traders</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center space-x-2">
                <Search className="w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search listings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64"
                />
              </div>
              <Select value={filterCurrency} onValueChange={setFilterCurrency}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Currencies</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="offer">Offers</SelectItem>
                  <SelectItem value="request">Requests</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Listings */}
        <div className="grid gap-4">
          {filteredListings.map((listing) => (
            <Card key={listing.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-3">
                      <Badge variant={listing.type === 'offer' ? 'default' : 'secondary'}>
                        {listing.type === 'offer' ? 'Offering' : 'Requesting'}
                      </Badge>
                      <Badge className={getStatusColor(listing.status)}>
                        {listing.status}
                      </Badge>
                      <span className="text-sm text-gray-600">by @{listing.username}</span>
                    </div>
                    
                    <div className="flex items-center space-x-4 mb-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl font-bold">
                          {getCurrencySymbol(listing.currency)}{listing.amount}
                        </span>
                        <span className="text-lg text-gray-600">{listing.currency}</span>
                      </div>
                      
                      {listing.wantedAmount && listing.wantedCurrency && (
                        <>
                          <ArrowRightLeft className="w-4 h-4 text-gray-400" />
                          <div className="flex items-center space-x-2">
                            <span className="text-2xl font-bold">
                              {getCurrencySymbol(listing.wantedCurrency)}{listing.wantedAmount}
                            </span>
                            <span className="text-lg text-gray-600">{listing.wantedCurrency}</span>
                          </div>
                        </>
                      )}
                    </div>
                    
                    <p className="text-gray-700 mb-3">{listing.description}</p>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{getTimeRemaining(listing.expiresAt)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Users className="w-4 h-4" />
                        <span>{listing.matches} matches</span>
                      </div>
                      {listing.acceptPartial && (
                        <Badge variant="outline">Partial OK</Badge>
                      )}
                      {listing.onlyFullAmount && (
                        <Badge variant="outline">Full Amount Only</Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    <ExchangeMatch listing={listing} onMatchCreated={handleMatchCreated} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Create Listing Form */}
        {showCreateForm && (
          <Card 
            ref={createFormRef}
            className="border-2 border-blue-200 shadow-lg transition-all duration-500 animate-pulse-once"
            style={{
              animation: 'pulse 1s cubic-bezier(0.4, 0, 0.6, 1) 1'
            }}
          >
            <CardHeader>
              <CardTitle>Create Exchange Listing</CardTitle>
              <CardDescription>
                Post an offer or request to trade tokens with other users
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateListing} className="space-y-6">
                <Tabs value={formData.type} onValueChange={(value) => setFormData({...formData, type: value as 'offer' | 'request'})}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="offer">I'm Offering</TabsTrigger>
                    <TabsTrigger value="request">I'm Requesting</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="offer" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="amount">Amount to Offer</Label>
                        <Input
                          ref={firstInputRef}
                          id="amount"
                          type="number"
                          placeholder="100"
                          value={formData.amount}
                          onChange={(e) => setFormData({...formData, amount: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="currency">Currency</Label>
                        <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="GBP">GBP</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="wantedAmount">Want in Return</Label>
                        <Input
                          id="wantedAmount"
                          type="number"
                          placeholder="85"
                          value={formData.wantedAmount}
                          onChange={(e) => setFormData({...formData, wantedAmount: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="wantedCurrency">Currency</Label>
                        <Select value={formData.wantedCurrency} onValueChange={(value) => setFormData({...formData, wantedCurrency: value})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="GBP">GBP</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="request" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="amount">Amount Needed</Label>
                        <Input
                          id="amount"
                          type="number"
                          placeholder="50"
                          value={formData.amount}
                          onChange={(e) => setFormData({...formData, amount: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="currency">Currency</Label>
                        <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="GBP">GBP</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="wantedAmount">Will Pay</Label>
                        <Input
                          id="wantedAmount"
                          type="number"
                          placeholder="55"
                          value={formData.wantedAmount}
                          onChange={(e) => setFormData({...formData, wantedAmount: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="wantedCurrency">Currency</Label>
                        <Select value={formData.wantedCurrency} onValueChange={(value) => setFormData({...formData, wantedCurrency: value})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="GBP">GBP</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
                
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe your exchange request..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="timeLimit">Time Limit</Label>
                    <Select value={formData.timeLimit} onValueChange={(value) => setFormData({...formData, timeLimit: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1h">1 Hour</SelectItem>
                        <SelectItem value="6h">6 Hours</SelectItem>
                        <SelectItem value="1d">1 Day</SelectItem>
                        <SelectItem value="3d">3 Days</SelectItem>
                        <SelectItem value="1w">1 Week</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <Label className="block mb-2">Payment Options</Label>
                  <RadioGroup
                    value={formData.acceptPartial ? "partial" : "full"}
                    onValueChange={val => setFormData({
                      ...formData,
                      acceptPartial: val === "partial",
                      onlyFullAmount: val === "full"
                    })}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="partial" id="partial" />
                      <Label htmlFor="partial">Accept partial amounts</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="full" id="full" />
                      <Label htmlFor="full">Only accept full amount</Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Creating...' : 'Create Listing'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ExchangePage; 